import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Select, Card, Spin, message } from 'antd';
import axios from 'axios';
import { isLoggedIn } from '../../services/AuthService';
import './Reports.css';

const { Title } = Typography;
const { Option } = Select;

// Enterprise type colors
const ENTERPRISE_COLORS = {
    FARM: '#52c41a',
    TELECOMMUNICATION: '#1890ff',
    ELECTRICAL: '#faad14',
    TRANSPORT: '#722ed1',
    ORDINARY_BUILDING: '#eb2f96',
    NON_ORDINARY_BUILDING: '#f759ab'
};

const EventCostAnalysis = () => {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedEnterpriseType, setSelectedEnterpriseType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);

    // Load events on mount
    useEffect(() => {
        const loadEvents = async () => {
            if (!isLoggedIn()) return;

            setEventsLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}/events/`,
                    { params: { size: 100, sort: 'date,desc' } }
                );
                setEvents(response.data.content || []);
            } catch (error) {
                console.error('Error loading events:', error);
                message.error('Failed to load events');
            } finally {
                setEventsLoading(false);
            }
        };
        loadEvents();
    }, []);

    // Load analysis when event or enterprise type is selected
    useEffect(() => {
        const loadAnalysis = async () => {
            if (!selectedEvent) {
                setAnalysis(null);
                return;
            }

            setLoading(true);
            try {
                const params = {};
                if (selectedEnterpriseType) {
                    params.enterpriseType = selectedEnterpriseType;
                }
                const response = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}/reports/event-costs/${selectedEvent}`,
                    { params }
                );
                setAnalysis(response.data);
            } catch (error) {
                console.error('Error fetching cost analysis:', error);
                message.error('Failed to load cost analysis');
                setAnalysis(null);
            } finally {
                setLoading(false);
            }
        };
        loadAnalysis();
    }, [selectedEvent, selectedEnterpriseType]);

    // Format currency
    const formatCurrency = (value) => {
        if (value == null || value === 0) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="reports-container">
            <Title level={2} className="reports-title">Event Cost Analysis</Title>

            {/* Event selector */}
            <div className="reports-filters">
                <Select
                    placeholder="Select an event"
                    style={{ minWidth: 350 }}
                    loading={eventsLoading}
                    value={selectedEvent}
                    onChange={setSelectedEvent}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                >
                    {events.map(event => (
                        <Option key={event.id} value={event.id}>
                            {event.name} ({event.type})
                        </Option>
                    ))}
                </Select>

                <Select
                    placeholder="Enterprise type (optional)"
                    style={{ minWidth: 200 }}
                    value={selectedEnterpriseType}
                    onChange={setSelectedEnterpriseType}
                    allowClear
                >
                    <Option value="ORDINARY_BUILDING">Building</Option>
                    <Option value="NON_ORDINARY_BUILDING">Non-ordinary building</Option>
                    <Option value="TRANSPORT">Transport</Option>
                    <Option value="FARM">Farm</Option>
                    <Option value="TELECOMMUNICATION">Telecommunication</Option>
                    <Option value="ELECTRICAL">Electrical</Option>
                </Select>
            </div>

            {/* Loading indicator */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <Spin size="large" />
                </div>
            )}

            {/* Analysis content */}
            {!loading && analysis && (
                <>
                    {/* Event Info Card */}
                    <Card className="event-info-card" title="Event Information">
                        <div className="event-info-grid">
                            <div className="event-info-item">
                                <span className="event-info-label">Name</span>
                                <span className="event-info-value">{analysis.eventName}</span>
                            </div>
                            <div className="event-info-item">
                                <span className="event-info-label">Type</span>
                                <span className="event-info-value">
                                    {t(`events.type.${analysis.eventType}`, analysis.eventType)}
                                </span>
                            </div>
                            <div className="event-info-item">
                                <span className="event-info-label">Date</span>
                                <span className="event-info-value">{formatDate(analysis.eventDate)}</span>
                            </div>
                            <div className="event-info-item">
                                <span className="event-info-label">Location</span>
                                <span className="event-info-value">{analysis.eventLocation || '-'}</span>
                            </div>
                            <div className="event-info-item">
                                <span className="event-info-label">Total Damages</span>
                                <span className="event-info-value" style={{ color: '#1890ff' }}>
                                    {analysis.totalDamages || 0}
                                </span>
                            </div>
                        </div>
                    </Card>

                    {/* Cost Breakdown */}
                    <div className="cost-breakdown">
                        {/* By Cost Type */}
                        <Card className="cost-breakdown-card" title="Cost by Type">
                            {analysis.costsByType && analysis.costsByType.length > 0 ? (
                                <>
                                    {analysis.costsByType.map((item, index) => (
                                        <div key={index} className="cost-item">
                                            <span className="cost-item-category">{item.category}</span>
                                            <span className="cost-item-value">
                                                {formatCurrency(item.estimatedCost)}
                                                {item.approvedCost > 0 && (
                                                    <span style={{ color: '#52c41a', marginLeft: 8 }}>
                                                        ({formatCurrency(item.approvedCost)} approved)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="no-data-message">No cost data available</div>
                            )}
                        </Card>

                        {/* By Enterprise Type */}
                        <Card className="cost-breakdown-card" title="Cost by Enterprise Type">
                            {analysis.costsByEnterpriseType && analysis.costsByEnterpriseType.length > 0 ? (
                                <>
                                    {analysis.costsByEnterpriseType.map((item, index) => (
                                        <div key={index} className="cost-item">
                                            <span
                                                className="cost-item-category"
                                                style={{ color: ENTERPRISE_COLORS[item.category] || '#666' }}
                                            >
                                                {item.category}
                                            </span>
                                            <span className="cost-item-value">
                                                {formatCurrency(item.estimatedCost)}
                                                {item.approvedCost > 0 && (
                                                    <span style={{ color: '#52c41a', marginLeft: 8 }}>
                                                        ({formatCurrency(item.approvedCost)} approved)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="no-data-message">No cost data available</div>
                            )}
                        </Card>
                    </div>

                    {/* Total Summary */}
                    <Card style={{ marginTop: 24 }}>
                        <div className="total-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 18 }}>TOTAL ESTIMATED COST</span>
                            <span style={{ fontSize: 24, color: '#1890ff' }}>
                                {formatCurrency(analysis.totalEstimatedCost)}
                            </span>
                        </div>
                        {analysis.totalApprovedCost > 0 && (
                            <div className="total-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                <span style={{ fontSize: 18 }}>TOTAL APPROVED COST</span>
                                <span style={{ fontSize: 24, color: '#52c41a' }}>
                                    {formatCurrency(analysis.totalApprovedCost)}
                                </span>
                            </div>
                        )}
                    </Card>
                </>
            )}

            {/* No data message */}
            {!loading && !analysis && selectedEvent && (
                <div className="no-data-message">
                    No cost data available for this event.
                </div>
            )}

            {/* Initial message */}
            {!loading && !selectedEvent && (
                <div className="no-data-message">
                    Select an event to view cost analysis.
                </div>
            )}
        </div>
    );
};

export default EventCostAnalysis;
